import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiCallOut, EuiCheckbox, EuiFieldText, EuiFormRow, EuiLink, EuiListGroup, EuiListGroupItem, EuiLoadingSpinner, EuiSpacer, EuiText, EuiTitle } from '@elastic/eui'

import { useGetRetargetingCampaignsQuery, useUpdateCampaignAdvancedTargetingMutation, useUpdateCampaignTvTargetingMutation } from 'api/rtkQueryApi/platform/campaignsApi'
import { showSuccessToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'
import { DefaultDayParts } from 'components/targeters/DayPartPicker'
import { AdvancedTargetingType } from 'features/builders/internet/retail/builderInternetRetailSlice'
import { finishedLoadingCampaign } from 'features/campaigns/campaign/campaignSlice'
import { useWhiteLabel } from 'whiteLabel/WhiteLabelContext'

import { useCampaignEdit } from './AgencyV2EditCampaignPage/CampaignEditContext'

export const RetargetingDrawer: React.FC = () => {
  const [updateAdvancedTargeting, updateAdvancedTargetingQuery] = useUpdateCampaignAdvancedTargetingMutation()
  const [updateTvTargeting, updateTvTargetingQuery] = useUpdateCampaignTvTargetingMutation()
  const dispatch = useDispatch()
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const { campaign } = useSelector((state: RootState) => state.campaign)
  const whiteLabel = useWhiteLabel()
  const getCampaignsQuery = useGetRetargetingCampaignsQuery({ accountId: currentAccount?.id ?? '', isAgency: whiteLabel?.isAgencies() })
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([])
  const [targetEntireSite, setTargetEntireSite] = useState<boolean>(false)
  const [targetSpecificPages, setTargetSpecificPages] = useState<boolean>(false)
  const [websiteUrls, setWebsiteUrls] = useState<string[]>([])
  const [websiteUrl, setWebsiteUrl] = useState<string>()
  const { registerSave, markDirty } = useCampaignEdit()

  useEffect(() => {
    if (!campaign) {
      return
    }

    if (campaign.advancedTargeting) {
      setSelectedCampaignIds(campaign.advancedTargeting.retargetingCampaignIds ?? [])
      setTargetEntireSite(campaign.advancedTargeting.retargetingType === 'All')
      setTargetSpecificPages(campaign.advancedTargeting.retargetingType === 'Specific')
      setWebsiteUrls(campaign.advancedTargeting.urlParts ?? [])
    } else if (campaign.tvTargeting) {
      setSelectedCampaignIds(campaign.tvTargeting.retargetingCampaignIds ?? [])
      setTargetEntireSite(campaign.tvTargeting.retargetingType === 'All')
      setTargetSpecificPages(campaign.tvTargeting.retargetingType === 'Specific')
      setWebsiteUrls(campaign.tvTargeting.urlParts ?? [])
    }
  }, [campaign])

  const onCampaignSelected = (optionId: string) => {
    if (selectedCampaignIds.some(id => id === optionId)) {
      setSelectedCampaignIds(selectedCampaignIds.filter(id => id !== optionId))
    } else {
      setSelectedCampaignIds([...selectedCampaignIds, optionId])
    }
    markDirty('retargeting')
  }

  const onUrlAdded = () => {
    if (websiteUrlIsValid) {
      setWebsiteUrls([...websiteUrls.filter(url => url !== websiteUrl), websiteUrl])
      setWebsiteUrl('')
    }
  }

  const onUrlRemoved = (url: string) => {
    setWebsiteUrls(websiteUrls.filter(u => u !== url))
  }

  const onUpdateCampaign = useCallback(async () => {
    if (campaign) {
      if (campaign.advancedTargeting) {
        await updateAdvancedTargeting({
          accountId: currentAccount!.id,
          campaignId: campaign.id,
          advancedTargeting: {
            advancedTargetingType: AdvancedTargetingType.Retarget,
            locationType: 'USA',
            states: [],
            dmas: [],
            cities: [],
            zips: [],
            geoFences: [],
            geoCircles: [],
            geoRectangles: [],
            geoPolygons: [],
            audiences: [],
            keywords: [],
            inventorySettings: null,
            deviceSettings: null,
            frequency: null,
            frequencyUnit: null,
            uploadType: null,
            uploadedData: [],
            audienceType: 'Retargeting',
            retargetingType: targetEntireSite ? 'All' : targetSpecificPages ? 'Specific' : null,
            urlParts: targetSpecificPages ? websiteUrls : [],
            retargetingCampaignIds: selectedCampaignIds,
            dayParts: DefaultDayParts,
            ageRanges: [],
            genders: [],
            householdIncomes: [],
            negativeKeywords: []
          }
        }).then(response => {
          if (!(response as any).error) {
            dispatch(showSuccessToast('Saved targeting information'))
            dispatch(finishedLoadingCampaign((response as any).data))
          }
        })
      } else if (campaign.tvTargeting) {
        await updateTvTargeting({
          accountId: currentAccount!.id,
          campaignId: campaign.id,
          tvTargeting: {
            tvTargetingType: 'Retarget',
            locationType: 'USA',
            states: [],
            dmas: [],
            cities: [],
            zips: [],
            audiences: [],
            ageRanges: [],
            genders: [],
            householdIncomes: [],
            frequency: null,
            frequencyUnit: null,
            dayParts: DefaultDayParts,
            retargetingCampaignIds: selectedCampaignIds,
            retargetingType: targetEntireSite ? 'All' : targetSpecificPages ? 'Specific' : undefined,
            urlParts: targetSpecificPages ? websiteUrls : []
          }
        })
      }
    }
  }, [campaign, currentAccount, selectedCampaignIds, targetEntireSite, targetSpecificPages, websiteUrls, updateAdvancedTargeting, updateTvTargeting, dispatch])

  useEffect(() => {
    registerSave('retargeting', onUpdateCampaign)
  }, [onUpdateCampaign, registerSave])

  const websiteUrlIsValid = websiteUrl && websiteUrl.length > 0 && websiteUrl.startsWith('https://')

  return (
    <React.Fragment>
      <EuiSpacer />
      <EuiTitle size='s'>
        <h3>Retarget my campaigns</h3>
      </EuiTitle>
      <EuiText size='s'>Select the campaigns you want to retarget.</EuiText>
      {getCampaignsQuery.data && getCampaignsQuery.data.length === 0 && (
        <EuiCallOut title='You have no campaigns to retarget' color={'primary'}>
          <p>Retargeting requires a previously active TV or Internet campaign to have generated an audience.</p>
        </EuiCallOut>
      )}
      <EuiSpacer />
      <EuiFormRow>
        <div style={{ maxHeight: 600, overflow: 'auto' }}>{getCampaignsQuery.data ? getCampaignsQuery.data.map(campaign => <EuiCheckbox key={campaign.id} id={campaign.id} label={`${campaign.name} ($${campaign.budget} ${campaign.type})`} checked={selectedCampaignIds.some(id => id === campaign.id)} onChange={() => onCampaignSelected(campaign.id)} />) : getCampaignsQuery.isLoading ? <EuiLoadingSpinner /> : <React.Fragment />}</div>
      </EuiFormRow>
      <EuiSpacer />
      <EuiTitle size='s'>
        <h3>Retarget my website</h3>
      </EuiTitle>
      <EuiText size='s'>Select how you want to retarget your website</EuiText>
      {!currentAccount?.hasVisits && (
        <EuiCallOut title='The Audience Pixel must be installed onto the site you wish to retarget' color={'primary'}>
          <p>
            To install the audience pixel and enable website retargeting, check out the{' '}
            <EuiLink target='_blank' href={'/audience-pixel'} color={'accent'}>
              Audience Pixel Tab
            </EuiLink>
          </p>
        </EuiCallOut>
      )}
      <EuiSpacer />
      <EuiFormRow label={'Target an audience generated from your website'}>
        <React.Fragment>
          <EuiCheckbox
            id='website'
            label='Entire Site'
            checked={targetEntireSite}
            onChange={() => {
              setTargetEntireSite(!targetEntireSite)
              markDirty('retargeting')
            }}
            disabled={targetSpecificPages || !currentAccount?.hasVisits}
          />
          <EuiCheckbox
            id='specificPages'
            label='Specific Pages'
            checked={targetSpecificPages}
            onChange={() => {
              setTargetSpecificPages(!targetSpecificPages)
              markDirty('retargeting')
            }}
            disabled={targetEntireSite || !currentAccount?.hasVisits}
          />
        </React.Fragment>
      </EuiFormRow>
      {targetSpecificPages && (
        <React.Fragment>
          <EuiFormRow label={"Add the url's of the pages you would like to target"} helpText={!!websiteUrl && websiteUrl.length > 0 && !websiteUrlIsValid ? 'Url must begin with https://' : undefined}>
            <EuiFieldText
              placeholder='https://'
              value={websiteUrl}
              onChange={e => {
                setWebsiteUrl(e.target.value)
              }}
              isInvalid={!!websiteUrl && websiteUrl.length > 0 && !websiteUrlIsValid}
              append={
                <EuiButton size='s' disabled={!websiteUrlIsValid} onClick={onUrlAdded}>
                  Add
                </EuiButton>
              }
            />
          </EuiFormRow>
          <EuiListGroup>
            {websiteUrls.map(url => (
              <EuiListGroupItem
                key={url}
                label={url}
                extraAction={{
                  color: 'text',
                  onClick: () => onUrlRemoved(url),
                  iconType: 'cross',
                  iconSize: 's',
                  'aria-label': 'Favorite link2',
                  alwaysShow: true
                }}
              />
            ))}
          </EuiListGroup>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}
